/**
 * SecretActionButtons Component
 *
 * Renders action buttons for a secret with permission-based visibility.
 * Checks authorization before showing view/edit/delete buttons.
 */

import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { authorizationApi } from '@/lib/pillarApi';
import { useCredentialUsages } from '@/lib/hooks/useSecrets';
import { AuthorizableAction, ResourceType } from '@pillar-portal/database/dist/frontend';
import { useAuthStore } from '@/store/auth';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { Eye, Edit, Trash2, Ban, Check, Unlink } from 'lucide-react';
import { resourceDesignatorKey, type ResourceDesignator, type SecretListItem, type ThirdPartySystemId } from '@pillar-portal/api-types';

export interface SecretActionButtonsCredential {
    credential_uuid: string;
    account_id: string;
    credential_type: SecretListItem['credential_type'];
    is_active: boolean;
    applies_to_children: boolean;
    system_id: ThirdPartySystemId;
}

interface SecretActionButtonsProps {
    secret: SecretActionButtonsCredential;
    onView: () => void;
    onEdit: () => void;
    onDisable: () => void;
    onEnable: () => void;
    onDelete: () => void;
    onDisconnect: () => void;
}

export function SecretActionButtons({
    secret,
    onView,
    onEdit,
    onDisable,
    onEnable,
    onDelete,
    onDisconnect,
}: SecretActionButtonsProps) {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const targetResource: ResourceDesignator = {
        resource_type: ResourceType.CREDENTIAL,
        credential_uuid: secret.credential_uuid,
    };
    const targetResourceKey: string = resourceDesignatorKey(targetResource);
    const { data: credentialUsagesResponse, isLoading: isCredentialUsagesLoading } = useCredentialUsages(
        {
            credential_uuid: secret.credential_uuid,
        }
    );
    const usageCount: number = credentialUsagesResponse?.usages.length ?? 0;
    const isInUse: boolean = usageCount > 0;

    // Check DISPLAY permission for viewing (to show decrypted credentials)
    const { data: canDisplay } = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'authorization',
            'is_authorized',
            AuthorizableAction.DISPLAY,
            ResourceType.CREDENTIAL,
            targetResourceKey,
        ]),
        queryFn: async () => {
            const response = await authorizationApi.isAuthorized({
                action: AuthorizableAction.DISPLAY,
                resource_type: ResourceType.CREDENTIAL,
                target_resource: targetResource,
            });
            return response.is_authorized;
        },
        enabled: true,
    });

    // Check EDIT permission for editing and enable/disable
    const { data: canEdit } = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'authorization',
            'is_authorized',
            AuthorizableAction.EDIT,
            ResourceType.CREDENTIAL,
            targetResourceKey,
        ]),
        queryFn: async () => {
            const response = await authorizationApi.isAuthorized({
                action: AuthorizableAction.EDIT,
                resource_type: ResourceType.CREDENTIAL,
                target_resource: targetResource,
            });
            return response.is_authorized;
        },
        enabled: true,
    });

    // Check DELETE permission for hard delete
    const { data: canDelete } = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'authorization',
            'is_authorized',
            AuthorizableAction.DELETE,
            ResourceType.CREDENTIAL,
            targetResourceKey,
        ]),
        queryFn: async () => {
            const response = await authorizationApi.isAuthorized({
                action: AuthorizableAction.DELETE,
                resource_type: ResourceType.CREDENTIAL,
                target_resource: targetResource,
            });
            return response.is_authorized;
        },
        enabled: true,
    });

    return (
        <div className="flex gap-2 ml-4">
            {canDisplay && (
                <Button variant="outline" size="sm" onClick={onView} title="View secret details">
                    <Eye className="h-4 w-4" />
                </Button>
            )}
            {canEdit && (
                <>
                    <Button variant="outline" size="sm" onClick={onEdit} title="Edit secret">
                        <Edit className="h-4 w-4" />
                    </Button>
                    {secret.is_active ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onDisable}
                            title="Disable secret"
                        >
                            <Ban className="h-4 w-4" />
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onEnable}
                            title="Enable secret"
                            className="text-green-700 hover:text-green-800"
                        >
                            <Check className="h-4 w-4" />
                        </Button>
                    )}
                </>
            )}
            {canDelete && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={isInUse ? () => undefined : onDelete}
                    title={
                        isInUse
                            ? 'Cannot delete this credential because it is currently used.'
                            : 'Delete secret'
                    }
                    className={`text-red-600 hover:text-red-700${
                        isInUse ? ' opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={isCredentialUsagesLoading}
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            )}
            {canEdit && isInUse && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onDisconnect}
                    title="Disconnect this credential from selected usages"
                >
                    <Unlink className="h-4 w-4" />
                </Button>
            )}
        </div>
    );
}
